import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, RefreshCw, Plug, PowerOff, Send, QrCode,
  CheckCircle2, AlertTriangle, Smartphone,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  connected: { label: '接続中', color: 'bg-emerald-600', icon: CheckCircle2 },
  qr_ready: { label: 'QR待機中', color: 'bg-amber-600', icon: QrCode },
  disconnected: { label: '未接続', color: 'bg-zinc-600', icon: PowerOff },
  connecting: { label: '接続中…', color: 'bg-blue-600', icon: Loader2 },
  unavailable: { label: 'サービス停止', color: 'bg-red-600', icon: AlertTriangle },
};

export default function WhatsAppSettingsPanel() {
  const [status, setStatus] = useState('unavailable');
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [qr, setQr] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/whatsapp/status`, { withCredentials: true });
      setAvailable(res.data.available !== false);
      setStatus(res.data.status || 'unavailable');
      if (res.data.status === 'connected') setQr(null);
    } catch {
      setAvailable(false);
      setStatus('unavailable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const fetchQr = useCallback(async () => {
    setQrLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/whatsapp/qr`, { withCredentials: true });
      setQr(res.data.qr || null);
      if (!res.data.qr) toast.info(res.data.message || 'QRは現在利用できません');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'QR取得に失敗しました');
    } finally {
      setQrLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === 'qr_ready') fetchQr();
  }, [status, fetchQr]);

  const doConnect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/whatsapp/connect`, {}, { withCredentials: true });
      toast.success('接続を開始しました。QRを取得します…');
      setTimeout(fetchQr, 1500);
      setTimeout(fetchStatus, 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || '接続に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const doDisconnect = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/whatsapp/disconnect`, {}, { withCredentials: true });
      toast.success('切断しました');
      setQr(null);
      fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || '切断に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const doTest = async () => {
    if (!testPhone.trim()) { toast.error('番号を入力してください'); return; }
    setTesting(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/whatsapp/test`,
        { phone: testPhone }, { withCredentials: true });
      toast.success(`テスト送信しました: ${res.data.sent_to}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'テスト送信に失敗しました');
    } finally {
      setTesting(false);
    }
  };

  const meta = STATUS_META[status] || STATUS_META.unavailable;
  const StatusIcon = meta.icon;
  const isConnected = status === 'connected';

  return (
    <div className="space-y-6" data-testid="wa-settings-panel">
      {/* Shared service warning */}
      <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-3" data-testid="wa-shared-warning">
        <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-200/90 leading-relaxed">
          この接続は共有WhatsAppサービスを利用しています。番号を切り替える場合は「切断」→「接続」でQRを表示し、希望の番号でスキャンしてください。切断・再ログインは他システムの送信にも影響する可能性があります。
        </p>
      </div>

      {/* Status Card */}
      <Card className="bg-[#111] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center justify-between">
            <span>接続状態</span>
            <Button size="sm" variant="ghost" onClick={fetchStatus} className="text-zinc-400 hover:text-white h-7" data-testid="wa-refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
            ) : (
              <>
                <Badge className={`${meta.color} text-white text-xs`} data-testid="wa-status-badge">
                  <StatusIcon className={`w-3 h-3 mr-1 ${status === 'connecting' ? 'animate-spin' : ''}`} />
                  {meta.label}
                </Badge>
                <span className="text-xs text-zinc-500">
                  {available ? 'サービス稼働中' : 'サービスに接続できません（本番環境でのみ利用可能）'}
                </span>
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {!isConnected && (
              <Button size="sm" onClick={doConnect} disabled={busy || !available}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="wa-connect-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plug className="w-3.5 h-3.5 mr-1" />}
                接続 / QR表示
              </Button>
            )}
            {isConnected && (
              <Button size="sm" variant="outline" onClick={doDisconnect} disabled={busy}
                className="text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid="wa-disconnect-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PowerOff className="w-3.5 h-3.5 mr-1" />}
                切断（番号を変更）
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* QR Card */}
      {!isConnected && (
        <Card className="bg-[#111] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <QrCode className="w-4 h-4" /> QRコードでログイン
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-zinc-400 leading-relaxed">
              スマートフォンのWhatsApp →「設定」→「リンク済みデバイス」→「デバイスをリンク」から下のQRをスキャンしてください。
            </p>
            <div className="flex flex-col items-center gap-3">
              {qrLoading ? (
                <div className="w-56 h-56 flex items-center justify-center bg-white/5 rounded-md">
                  <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
                </div>
              ) : qr ? (
                <img src={qr} alt="WhatsApp QR" className="w-56 h-56 rounded-md bg-white p-2" data-testid="wa-qr-img" />
              ) : (
                <div className="w-56 h-56 flex flex-col items-center justify-center gap-2 bg-white/5 rounded-md text-zinc-600">
                  <Smartphone className="w-8 h-8" />
                  <span className="text-xs">QR未取得</span>
                </div>
              )}
              <Button size="sm" variant="outline" onClick={fetchQr} disabled={qrLoading || !available}
                className="text-zinc-300 border-white/20" data-testid="wa-qr-refresh-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> QRを更新
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Send Card */}
      <Card className="bg-[#111] border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Send className="w-4 h-4" /> テスト送信
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-zinc-400">指定した番号に動作確認メッセージを送信します（国番号付き）。</p>
          <div className="flex gap-2">
            <Input value={testPhone} onChange={e => setTestPhone(e.target.value)}
              placeholder="55 11 99999-9999" className="bg-black/40 border-white/10 text-white h-9 text-sm"
              data-testid="wa-test-phone" />
            <Button onClick={doTest} disabled={testing || !isConnected}
              className="bg-white text-black hover:bg-zinc-200 shrink-0" data-testid="wa-test-btn">
              {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              送信
            </Button>
          </div>
          {!isConnected && (
            <p className="text-xs text-amber-400/80">※ 接続中のときのみ送信できます。</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
